/**
 * Collapsible list of incomplete past-due todos. The screen owns the
 * filter/sort (incomplete, dueDate < today, oldest first); this component
 * just presents it. Expanded by default — surfacing stragglers is the
 * section's whole job — and renders nothing when the list is empty.
 * Rows reuse TodoRow with a dateLabel so each item shows how old it is;
 * completing a row removes it from the list upstream, which is the feedback.
 */
import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { formatDayHeading, parseDateKey } from '@/utils/residents/todoCalendar';
import type { ResidentTodo } from '@/types/residents/todos';
import { TodoRow } from './TodoRow';

interface OverdueSectionProps {
  /** Already filtered (incomplete, past-due) and sorted oldest-first. */
  todos: ResidentTodo[];
  onToggle: (id: string) => void;
  onEdit: (todo: ResidentTodo) => void;
}

export const OverdueSection: React.FC<OverdueSectionProps> = ({
  todos,
  onToggle,
  onEdit,
}) => {
  const [expanded, setExpanded] = useState(true);
  const type = useTypography();
  const space = useSpacing();
  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.sm,
        minHeight: 44,
      },
      warnIcon: {
        color: semantic.status.warning,
      },
      headerText: {
        ...type.cardTitle,
        color: colors.text.primary,
        flex: 1,
      },
      chevron: {
        color: colors.text.muted,
      },
    }),
  );

  if (todos.length === 0) return null;

  const a11yLabel = `Overdue, ${todos.length} ${todos.length === 1 ? 'todo' : 'todos'}`;

  return (
    <View>
      <TouchableOpacity
        style={styles.headerRow}
        onPress={() => setExpanded(e => !e)}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={a11yLabel}
      >
        <Icon name="alert-circle-outline" size={18} style={styles.warnIcon} />
        <Text style={styles.headerText}>{`Overdue · ${todos.length}`}</Text>
        <Icon
          name={expanded ? 'chevron-down' : 'chevron-forward'}
          size={16}
          style={styles.chevron}
        />
      </TouchableOpacity>
      {expanded && (
        <View>
          {todos.map((todo, index) => (
            <TodoRow
              key={todo.id}
              todo={todo}
              isLast={index === todos.length - 1}
              onToggle={onToggle}
              onEdit={onEdit}
              dateLabel={formatDayHeading(parseDateKey(todo.dueDate))}
            />
          ))}
        </View>
      )}
    </View>
  );
};

OverdueSection.displayName = 'OverdueSection';
